import React, { useState } from 'react';
import PropTypes from 'prop-types';

import './ClusterScatterplot.css';

import { useAppState } from '../../AppContext';

import { pointStyle } from './ClusterScatterplotStyle';
const d3 = Object.assign({}, require('d3-scale'), require('d3-array'));

const ClusterScatterplotMinimap = ({
  highlightSnippet,
  filterDocument,
  svgStyle
}) => {
  const scatterplotSize = 1000;

  let [
    {
      documents,
      selectedDocument,
      clusterDataCopy: clusterData,
      selectedCluster,
      documentResults,
      mousedOverCluster,
      mousedOverDocument,
      documentsInTable
    },
    dispatch
  ] = useAppState();

  const [corner1, setCorner1] = useState(null);
  const [corner2, setCorner2] = useState(null);

  const [isMakingSelection, setIsMakingSelection] = useState(false); // records whether user is currently dragging to select a rectangle

  const [initialClickPosition, setInitialClickPosition] = useState(null);
  const [initialCorner1, setInitialCorner1] = useState(null);
  const [initialCorner2, setInitialCorner2] = useState(null);

  const documentFromId = (id) => documents.filter((doc) => doc.id === id)[0];

  const svgRef = React.createRef();

  let resultDocumentIds = [];
  if (documentResults) {
    resultDocumentIds = documentResults.map((d) => +d.id);
  }

  // flatten cluster data into a single array of objects, rather than one array per cluster
  let flatClusters = [];
  for (let i = 0; i < clusterData.length; i++) {
    flatClusters = flatClusters.concat(
      clusterData[i].members.map((doc) => ({
        ...doc,
        cluster_index: i,
        cluster_id: clusterData[i].id
      }))
    );
  }

  const x = d3
    .scaleLinear()
    .domain(d3.extent(flatClusters.map((d) => d.x)))
    .range([5, scatterplotSize - 5]);
  const y = d3
    .scaleLinear()
    .domain(d3.extent(flatClusters.map((d) => d.y)))
    .range([5, scatterplotSize - 5]);

  const getPosition = (ev) => {
    const svgBB = svgRef.current.getBoundingClientRect();
    const scaling = svgRef.current ? scatterplotSize / svgBB.width : 1;

    const xpos = x.invert((ev.clientX - svgBB.x) * scaling);
    const ypos = y.invert((ev.clientY - svgBB.y) * scaling);
    return [xpos, ypos];
  };

  const handleMouseOut = (ev) => {
    // Mouseup events fire not only when the cursor moves past the edge of the SVG
    // but also when it moves over a point on the scatterplot.
    // Only the first of these should terminate the selection.

    const [xpos, ypos] = getPosition(ev);

    if (xpos <= x.domain()[0] || xpos >= x.domain()[1]) {
      handleMouseUp(ev);
    } else if (ypos <= y.domain()[0] || ypos >= y.domain()[1]) {
      handleMouseUp(ev);
    }
  };

  const handleMouseDown = (ev) => {
    const pos = getPosition(ev);
    setCorner1(pos);
    setCorner2(null);
    setIsMakingSelection(true);
  };

  const handleMouseMove = (ev) => {
    if (isMakingSelection) {
      // We're dragging second corner to create new rect
      const pos = getPosition(ev);

      setCorner2(pos);
    } else if (initialClickPosition !== null) {
      // We're dragging an existing rectangle
      const pos = getPosition(ev);

      const difference = [
        initialClickPosition[0] - pos[0],
        initialClickPosition[1] - pos[1]
      ];

      setCorner1([
        initialCorner1[0] - difference[0],
        initialCorner1[1] - difference[1]
      ]);
      setCorner2([
        initialCorner2[0] - difference[0],
        initialCorner2[1] - difference[1]
      ]);
    }
  };

  const handleMouseUp = (ev) => {
    if (!isMakingSelection && initialClickPosition == null) {
      return;
    }

    const pos = getPosition(ev);

    if (initialClickPosition !== null) {
      setInitialClickPosition(null);

      const min_x = Math.min(corner1[0], corner2[0]);
      const min_y = Math.min(corner1[1], corner2[1]);

      const max_x = Math.max(corner1[0], corner2[0]);
      const max_y = Math.max(corner1[1], corner2[1]);

      dispatch({
        type: 'SET_APPLIED_BRUSH',
        appliedBrush: [
          [min_x, min_y],
          [max_x, max_y]
        ]
      });
    } else {
      // corner2 might not yet have been set to pos...
      setCorner2(pos);
      setIsMakingSelection(false);

      const min_x = Math.min(corner1[0], pos[0]);
      const min_y = Math.min(corner1[1], pos[1]);

      const max_x = Math.max(corner1[0], pos[0]);
      const max_y = Math.max(corner1[1], pos[1]);

      if (min_x === max_x && min_y === max_y) {
        dispatch({
          type: 'SET_APPLIED_BRUSH',
          appliedBrush: null
        });
      } else {
        dispatch({
          type: 'SET_APPLIED_BRUSH',
          appliedBrush: [
            [min_x, min_y],
            [max_x, max_y]
          ]
        });
      }
    }
  };

  const html = document.getElementsByTagName('html')[0];
  const displayedDocIds = documentsInTable.map((d) => d.id);

  return (
    <>
      <svg
        viewBox={`0 0 ${scatterplotSize} ${scatterplotSize}`}
        ref={svgRef}
        /* Limit size of SVG to half of width and height so it fits in left column, and some of PDF preview is visible */
        style={
          svgStyle
            ? svgStyle
            : {
                maxWidth: Math.min(html.clientHeight / 2, html.clientWidth),
                display: 'block',
                margin: 'auto'
              }
        }
        id="minimapSVG"
        onMouseOut={handleMouseOut}
      >
        {/* Background rect to provide a click target */}\
        <rect
          x={0}
          y={0}
          width={scatterplotSize}
          height={scatterplotSize}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          fill="white"
          stroke="black"
          strokeWidth="2px"
        />
        {/* Points for individual documents */}
        {flatClusters.map((d, i) => {
          const document = documentFromId(d.document_id);

          // A document may have been deleted since clustering was performed
          if (!document) {
            return null;
          }

          const { color, radius, opacity } = pointStyle(
            document,
            selectedDocument,
            mousedOverDocument,
            mousedOverCluster,
            clusterData,
            d.cluster_id,
            null, //nearestDocument,
            selectedCluster,
            displayedDocIds,
            documentsInTable.length < documents.length
          );

          return (
            <circle
              cx={x(d.x)}
              cy={y(d.y)}
              r={radius}
              fill={color}
              stroke={
                resultDocumentIds.includes(d.document_id) ? 'black' : 'none'
              }
              strokeWidth={'3px'}
              opacity={opacity}
              y={i}
              key={i}
            />
          );
        })}
        {
          /* Brush overlay */
          corner1 && corner2 && (
            <rect
              fill="black"
              opacity="0.2"
              x={Math.min(x(corner1[0]), x(corner2[0]))}
              y={Math.min(y(corner1[1]), y(corner2[1]))}
              width={Math.abs(x(corner2[0]) - x(corner1[0]))}
              height={Math.abs(y(corner2[1]) - y(corner1[1]))}
              onMouseDown={(ev) => {
                const pos = getPosition(ev);
                setInitialClickPosition(pos);
                setInitialCorner1(corner1);
                setInitialCorner2(corner2);
              }}
              onMouseUp={handleMouseUp}
              style={{ cursor: 'move' }}
            />
          )
        }
      </svg>
    </>
  );
};

ClusterScatterplotMinimap.propTypes = {
  highlightSnippet: PropTypes.func,
  filterDocument: PropTypes.func,
  svgStyle: PropTypes.object
};

export default ClusterScatterplotMinimap;
